import { useSuspenseQuery } from '@apollo/client';
import { ChevronDown, FileCodeIcon, TriangleAlertIcon } from 'lucide-react';
import { Suspense, useState } from 'react';

import { TooltipLabel } from '@eluve/blocks';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  HStack,
  Label,
  P,
  Skeleton,
  VStack,
  legacyTextStyles,
} from '@eluve/components';
import { getLatestTranscriptQuery } from '@eluve/frontend-appointment-hooks';

interface LatestTranscriptMetadataProps {
  appointmentId: string;
  tenantId: string;
}

const LatestTranscriptMetadataContent: React.FC<
  LatestTranscriptMetadataProps
> = ({ appointmentId, tenantId }) => {
  const { data } = useSuspenseQuery(getLatestTranscriptQuery, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
    },
  });

  const transcripts = data?.appointmentsByPk?.transcripts;
  const newestTranscript =
    transcripts && transcripts.length > 0
      ? [...transcripts].sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )[0]
      : null;

  if (!newestTranscript) {
    return (
      <CollapsibleContent>
        <HStack
          gap={3}
          className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-sm text-text-warning-subtle"
        >
          <TriangleAlertIcon className="size-5" />
          No batch transcriptions exist for this appointment.
        </HStack>
      </CollapsibleContent>
    );
  }

  return (
    <CollapsibleContent>
      <HStack
        gap={0}
        className="rounded-lg border border-border-quaternary text-sm text-text-secondary"
      >
        <HStack gap={2} className="flex-1 items-center p-2">
          <TooltipLabel label="Prompt Template">
            <FileCodeIcon className="mx-1 size-6" />
          </TooltipLabel>
          {newestTranscript.transcriptionSource}
        </HStack>
      </HStack>
    </CollapsibleContent>
  );
};

export const LatestTranscriptMetadata: React.FC<
  LatestTranscriptMetadataProps
> = ({ appointmentId, tenantId }) => {
  const [opened, setOpened] = useState(false);

  return (
    <Collapsible open={opened} onOpenChange={setOpened}>
      <VStack className="space-y-2">
        <HStack>
          <Label className="px-1">Transcript Metadata</Label>
          <CollapsibleTrigger>
            <HStack>
              <ChevronDown
                className='size-5 transition-transform duration-300 ease-in-out data-[state="open"]:-rotate-180'
                data-state={opened ? 'open' : 'closed'}
              />
              <P className={legacyTextStyles.body({ size: 'm' })}>
                {!opened ? 'Show' : 'Hide'}
              </P>
            </HStack>
          </CollapsibleTrigger>
        </HStack>
        <Suspense
          fallback={
            <CollapsibleContent>
              <HStack className="rounded-lg border border-border-quaternary p-4">
                <Skeleton className="h-5 w-full" />
              </HStack>
            </CollapsibleContent>
          }
        >
          <LatestTranscriptMetadataContent
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </Suspense>
      </VStack>
    </Collapsible>
  );
};
